import React, { useState } from 'react';
import styled from 'styled-components';
import { 
  Card, 
  CardHeader, 
  CardBody,
  Button,
  FormGroup,
  Label,
  Input,
  ErrorText
} from '../../styles/StyledComponents';
import { useAuth } from '../../context/AuthContext';

const ProfileContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const FlexRow = styled.div`
  display: flex;
  gap: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Column = styled.div`
  flex: 1;
`;

const Divider = styled.div`
  width: 1px;
  background: var(--border-color);
  margin: 0 1rem;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const ProfileSection = styled(Card)`
  margin-bottom: 2rem;
`;

const ContactForm = styled.form`
  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    margin-bottom: 1rem;
    
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }
`;

const PasswordForm = styled.form`
  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    margin-bottom: 1rem;
    
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }
`;

const SuccessText = styled.div`
  color: var(--success-color);
  background-color: #d4edda;
  border: 1px solid #c3e6cb;
  border-radius: var(--border-radius);
  padding: 0.75rem;
  margin-bottom: 1rem;
  font-size: 0.9rem;
`;

const StoreKeeperProfile: React.FC = () => {
  const { user } = useAuth();
  const [contactForm, setContactForm] = useState({
    phone: (user as any)?.contactDetails?.phone || '',
    address: (user as any)?.contactDetails?.address || '',
    department: (user as any)?.contactDetails?.department || 'Store Management'
  });
  
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  const [contactErrors, setContactErrors] = useState<{[key: string]: string}>({});
  const [passwordErrors, setPasswordErrors] = useState<{[key: string]: string}>({});
  const [contactSuccess, setContactSuccess] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  const handleContactChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setContactForm(prev => ({ ...prev, [name]: value }));
    if (contactErrors[name]) {
      setContactErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({ ...prev, [name]: value }));
    if (passwordErrors[name]) {
      setPasswordErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateContactForm = () => {
    const errors: {[key: string]: string} = {};
    
    if (!contactForm.phone.trim()) {
      errors.phone = 'Phone number is required';
    }
    
    if (!contactForm.address.trim()) {
      errors.address = 'Address is required';
    }
    
    setContactErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validatePasswordForm = () => {
    const errors: {[key: string]: string} = {};
    
    if (!passwordForm.currentPassword.trim()) {
      errors.currentPassword = 'Current password is required';
    }
    
    if (!passwordForm.newPassword.trim()) {
      errors.newPassword = 'New password is required';
    } else if (passwordForm.newPassword.length < 8) {
      errors.newPassword = 'Password must be at least 8 characters';
    }
    
    if (!passwordForm.confirmPassword.trim()) {
      errors.confirmPassword = 'Confirm password is required';
    } else if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleContactSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateContactForm()) {
      // In a real application, you would call an API here
      setContactSuccess(true);
      setTimeout(() => setContactSuccess(false), 3000);
    }
  };

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validatePasswordForm()) {
      // In a real application, you would call an API here
      setPasswordSuccess(true);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => setPasswordSuccess(false), 3000);
    }
  };

  const formatDate = (date: Date | undefined) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <ProfileContainer>
      <h1 className="page-title">Store Keeper Profile</h1>
      
      <FlexRow>
        <Column>
          <ProfileSection>
            <CardHeader>
              <h3>Profile Information</h3>
            </CardHeader>
            <CardBody>
              <div style={{ marginBottom: '1rem' }}>
                <strong>Name:</strong> {user?.name}
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <strong>Email:</strong> {user?.email}
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <strong>Role:</strong> {user?.role}
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <strong>Status:</strong> {(user as any)?.status || 'Active'}
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <strong>Last Login:</strong> {formatDate((user as any)?.updatedAt)}
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <strong>Member Since:</strong> {formatDate((user as any)?.createdAt)}
              </div>
            </CardBody>
          </ProfileSection>
        </Column>
        
        <Divider />
        
        <Column>
          <ProfileSection>
            <CardHeader>
              <h3>Edit Contact Details</h3>
            </CardHeader>
            <CardBody>
              {contactSuccess && (
                <SuccessText>Contact details updated successfully!</SuccessText>
              )}
              
              <ContactForm onSubmit={handleContactSubmit}>
                <FormGroup>
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={contactForm.phone}
                    onChange={handleContactChange}
                    placeholder="Enter phone number"
                  />
                  {contactErrors.phone && <ErrorText>{contactErrors.phone}</ErrorText>}
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="address">Address</Label>
                  <Input
                    type="text"
                    id="address"
                    name="address"
                    value={contactForm.address}
                    onChange={handleContactChange}
                    placeholder="Enter address"
                  />
                  {contactErrors.address && <ErrorText>{contactErrors.address}</ErrorText>}
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="department">Department</Label>
                  <Input
                    type="text"
                    id="department"
                    name="department"
                    value={contactForm.department}
                    onChange={handleContactChange}
                    placeholder="Enter department"
                  />
                  {contactErrors.department && <ErrorText>{contactErrors.department}</ErrorText>}
                </FormGroup>
                
                <Button type="submit">Update Contact Details</Button>
              </ContactForm>
            </CardBody>
          </ProfileSection>
        </Column>
      </FlexRow>
      
      <ProfileSection>
        <CardHeader>
          <h3>Change Password</h3>
        </CardHeader>
        <CardBody>
          {passwordSuccess && (
            <SuccessText>Password changed successfully!</SuccessText>
          )}
          
          <PasswordForm onSubmit={handlePasswordSubmit}>
            <div className="form-row">
              <FormGroup>
                <Label htmlFor="currentPassword">Current Password</Label>
                <Input
                  type="password"
                  id="currentPassword"
                  name="currentPassword"
                  value={passwordForm.currentPassword}
                  onChange={handlePasswordChange}
                  placeholder="Enter current password"
                />
                {passwordErrors.currentPassword && <ErrorText>{passwordErrors.currentPassword}</ErrorText>}
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="newPassword">New Password</Label>
                <Input
                  type="password"
                  id="newPassword"
                  name="newPassword"
                  value={passwordForm.newPassword}
                  onChange={handlePasswordChange}
                  placeholder="Enter new password"
                />
                {passwordErrors.newPassword && <ErrorText>{passwordErrors.newPassword}</ErrorText>}
              </FormGroup>
            </div>
            
            <FormGroup>
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <Input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={passwordForm.confirmPassword}
                onChange={handlePasswordChange}
                placeholder="Confirm new password"
              />
              {passwordErrors.confirmPassword && <ErrorText>{passwordErrors.confirmPassword}</ErrorText>}
            </FormGroup>
            
            <Button type="submit">Change Password</Button>
          </PasswordForm>
        </CardBody>
      </ProfileSection>
    </ProfileContainer>
  );
};

export default StoreKeeperProfile; 